/**
 * Add-ons UI state.
 *
 * Holds the marketplace catalog (with per-business state) and a cache of active
 * injection points keyed by `${businessId}:${target}:${placement}:${contextKey}`.
 * The injection cache lets AddonInjectionSlot render without flicker and avoids
 * refetching per mount.
 */
import { createSlice } from '@reduxjs/toolkit';

const emptyChrome = {
  active: false,
  title: '',
  icon: null,
  tabs: [], // [{ key, label }]
  activeTab: null,
  search: '',
  searchPlaceholder: '',
  showSearch: false,
  showRefresh: false,
  // Bouton d'action primaire (ex. "Ajouter") affiché par la page add-on dans la TopBar.
  showAddTime: false,
  showAdd: false,
  addLabel: '',
  addDisabled: false,
  // Compteur incrémenté par le bouton rafraîchir de la TopBar : la page add-on
  // observe ce nonce pour relancer son chargement (pas de callback en Redux).
  refreshNonce: 0,
  // Compteur incrémenté par le bouton d'ajout de la TopBar : la page add-on
  // observe ce nonce pour ouvrir son flux de création.
  addNonce: 0,
  // Boutons d'export CSV / impression HTML affichés par la page add-on dans la TopBar.
  showExport: false,
  showPrint: false,
  // Compteur incrémenté par les boutons export/imprimer de la TopBar : la page add-on
  // observe ce nonce pour générer le fichier dans le format demandé.
  exportNonce: 0,
  exportFormat: null, // 'csv' | 'print'
  // Typed filter controls an add-on page renders in the TopBar (serializable).
  // [{ type: 'weekNav'|'select'|'toggle'|'chips', key, ... }]
  filters: [],
  filterValues: {}, // key -> value
};

const initialState = {
  addons: [],
  loaded: false,
  loading: false,
  // Resolved capability set (plan + active add-ons), drives useCapability gating.
  capabilities: [],
  capabilitiesLoaded: false,
  capabilitiesLoading: false,
  injections: {}, // key: `${target}:${placement}` -> array of injection points
  injectionRequests: {}, // per-context loading/error; errors are serializable
  injectionGenerations: {}, // business/user scope -> explicit refresh generation
  injectionPermissions: {}, // business/user scope -> observed permission context
  // Chrome (title / tabs / search) an add-on full-page screen pushes to the global TopBar.
  pageChrome: { ...emptyChrome },
};

const addonsSlice = createSlice({
  name: 'addons',
  initialState,
  reducers: {
    setAddonsLoading: (state, action) => {
      state.loading = action.payload;
    },
    setAddons: (state, action) => {
      state.addons = action.payload || [];
      state.loaded = true;
      state.loading = false;
    },
    setCapabilitiesLoading: (state, action) => {
      state.capabilitiesLoading = action.payload;
    },
    setCapabilities: (state, action) => {
      state.capabilities = action.payload || [];
      state.capabilitiesLoaded = true;
      state.capabilitiesLoading = false;
    },
    upsertAddonState: (state, action) => {
      const updated = action.payload;
      if (!updated?.id) return;
      const idx = state.addons.findIndex((a) => a.id === updated.id);
      if (idx >= 0) state.addons[idx] = updated;
      else state.addons.push(updated);
    },
    setInjections: (state, action) => {
      const { key, points, scope, generation, permissionKey } = action.payload;
      if (scope && ((state.injectionGenerations?.[scope] || 0) !== generation || state.injectionPermissions?.[scope] !== permissionKey)) return;
      state.injections[key] = points || [];
      state.injectionRequests = state.injectionRequests || {};
      state.injectionRequests[key] = { loading: false, error: null };
    },
    setInjectionRequest: (state, action) => {
      const { key, scope, generation, permissionKey, loading, error } = action.payload;
      if ((state.injectionGenerations?.[scope] || 0) !== generation || state.injectionPermissions?.[scope] !== permissionKey) return;
      state.injectionRequests = state.injectionRequests || {};
      state.injectionRequests[key] = { loading: !!loading, error: error || null };
    },
    observeInjectionPermissions: (state, action) => {
      const { scope, permissionKey } = action.payload;
      state.injectionPermissions = state.injectionPermissions || {};
      state.injectionGenerations = state.injectionGenerations || {};
      state.injectionRequests = state.injectionRequests || {};
      const previous = state.injectionPermissions[scope];
      if (previous === permissionKey) return;
      state.injectionPermissions[scope] = permissionKey;
      if (previous === undefined) return;
      // The bootstrap discovery belongs to the old rights. Resolve again.
      state.injectionGenerations[scope] = (state.injectionGenerations[scope] || 0) + 1;
      for (const key of Object.keys(state.injections)) if (key.startsWith(`${scope}:`)) delete state.injections[key];
      for (const key of Object.keys(state.injectionRequests)) if (key.startsWith(`${scope}:`)) delete state.injectionRequests[key];
    },
    refreshAddonInjections: (state, action) => {
      const { scope, permissionKey } = action.payload;
      if (!scope) return;
      state.injectionPermissions = state.injectionPermissions || {};
      state.injectionGenerations = state.injectionGenerations || {};
      state.injectionRequests = state.injectionRequests || {};
      state.injectionPermissions[scope] = permissionKey;
      state.injectionGenerations[scope] = (state.injectionGenerations[scope] || 0) + 1;
      // Leave capabilities, page chrome and the open layout draft untouched.
      for (const key of Object.keys(state.injections)) if (key.startsWith(`${scope}:`)) delete state.injections[key];
      for (const key of Object.keys(state.injectionRequests)) if (key.startsWith(`${scope}:`)) delete state.injectionRequests[key];
    },
    setAddonPageChrome: (state, action) => {
      state.pageChrome = { ...emptyChrome, ...(action.payload || {}), active: true };
    },
    // Merge partiel : ne reset pas nonces / filters / filterValues.
    patchAddonPageChrome: (state, action) => {
      if (!state.pageChrome?.active) return;
      state.pageChrome = { ...state.pageChrome, ...(action.payload || {}) };
    },
    setAddonPageActiveTab: (state, action) => {
      state.pageChrome.activeTab = action.payload;
    },
    setAddonPageSearch: (state, action) => {
      state.pageChrome.search = action.payload;
    },
    bumpAddonPageRefresh: (state) => {
      state.pageChrome.refreshNonce = (state.pageChrome.refreshNonce || 0) + 1;
    },
    bumpAddonPageAdd: (state) => {
      state.pageChrome.addNonce = (state.pageChrome.addNonce || 0) + 1;
    },
    bumpAddonPageExport: (state, action) => {
      state.pageChrome.exportNonce = (state.pageChrome.exportNonce || 0) + 1;
      state.pageChrome.exportFormat = action.payload || 'csv';
    },
    setAddonPageFilters: (state, action) => {
      const { filters, filterValues } = action.payload || {};
      state.pageChrome.filters = filters || [];
      if (filterValues) state.pageChrome.filterValues = filterValues;
    },
    setAddonPageFilterValue: (state, action) => {
      const { key, value } = action.payload || {};
      if (!key) return;
      state.pageChrome.filterValues = { ...state.pageChrome.filterValues, [key]: value };
    },
    clearAddonPageChrome: (state) => {
      state.pageChrome = { ...emptyChrome };
    },
    resetAddons: () => initialState,
  },
});

export const {
  setAddonsLoading,
  setAddons,
  setCapabilitiesLoading,
  setCapabilities,
  upsertAddonState,
  setInjections,
  setInjectionRequest,
  observeInjectionPermissions,
  refreshAddonInjections,
  setAddonPageChrome,
  patchAddonPageChrome,
  setAddonPageActiveTab,
  setAddonPageSearch,
  bumpAddonPageRefresh,
  bumpAddonPageAdd,
  bumpAddonPageExport,
  setAddonPageFilters,
  setAddonPageFilterValue,
  clearAddonPageChrome,
  resetAddons,
} = addonsSlice.actions;

export default addonsSlice.reducer;
